import {Button, Paper, Table} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import { ERoutes } from 'src/types/enum/routes.enum';
import { ToS } from 'src/types/models/ToS';
import { userProfileActions } from 'src/store/reducers/user-reducer';
import useTosService from 'src/services/tos';
import useTranslation from 'src/i18next.config';

function ToSList() {

    // hooks
    const navigate = useNavigate();
    const tosService = useTosService();
    const {t} = useTranslation();
    const updateTosStatus = userProfileActions.useUpdateTosStatus();
    const userProfile = userProfileActions.useUserProfile();

    // state
    const [tosList, setTosList] = useState<ToS[]>([]);
    const tosArray = tosList ? tosList : [];

    const getSeeOrEditButton = (userProfile: any, tos: ToS) => {
        if (userProfile.isAdmin) {
            return (
                <Button
                    color="cyan.8"
                    variant="outline"
                    onClick={() => {
                        navigate(`/admin/tos/${tos._id}/edit`);
                    }}
                >
                    {t('edit')}
                </Button>
            );
        } else {
            return (
                <Button
                    color="cyan.8"
                    variant="outline"
                    onClick={() => {
                        navigate(`/org/tos/${tos._id}`);
                    }}
                >
                    {t('tos.seeToSBtn')}
                </Button>
            );
        }
    };

    useEffect(function () {
        if (tosList.length < 1) {
            tosService.getTosList({
                error: () => {
                    showNotification({
                        title: t('anErrorOccurredPleaseRetryLater'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: (fetchedToSList: ToS[]) => {
                    setTosList(fetchedToSList);
                    if (!userProfile.isAdmin && fetchedToSList.length > 0) {
                        const latestTos = fetchedToSList.reduce((prev, current) => (prev.version! > current.version!) ? prev : current);
                        const orgApprovedLatestToS = latestTos.approved === true;
                        const tosMustBeApproved = latestTos.approved === false && new Date(latestTos.intoEffectAt).getTime() < Date.now();
                        updateTosStatus(orgApprovedLatestToS, tosMustBeApproved);
                    }
                },
            });
        }
    }, []);

    const rows = tosArray
        .sort((a, b) => {
            if (a.version && b.version) {
                return b.version - a.version;
            } else {
                return 0;
            }
        })
        .map((tos: ToS) => {
        const displayedPublishedDate = tos.createdAt ? new Date(tos.createdAt).toLocaleDateString() : '';
        const displayedIntoEffectAt = tos.intoEffectAt ? new Date(tos.intoEffectAt).toLocaleDateString() : '';
        return (
            <Table.Tr key={tos._id}>
                <Table.Td>{tos.version ?? 1}</Table.Td>
                <Table.Td>{displayedPublishedDate}</Table.Td>
                <Table.Td>{displayedIntoEffectAt}</Table.Td>
                {userProfile.isOrganizationManager && <Table.Td>{tos.approved ? '\u2705' : '\u274C'}</Table.Td>}
                <Table.Td>
                    {getSeeOrEditButton(userProfile, tos)}
                </Table.Td>
            </Table.Tr>
        );
    });

    return (
        <>
            {userProfile.isAdmin && <Button
                color="cyan.8"
                type="submit"
                style={{marginBottom: 20}}
                onClick={() => {
                    navigate(ERoutes.ADMIN_ADD_TOS);
                }}
            >
                {t('tos.addToSBtn')}
            </Button>}
            <Paper shadow="md" style={{width: 'fit-content', margin: 'auto'}}>
                <Table.ScrollContainer minWidth={500}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{minWidth: '120px'}}>{t('tos.version')}</Table.Th>
                                <Table.Th style={{minWidth: '120px'}}>{t('tos.createdAt')}</Table.Th>
                                <Table.Th style={{minWidth: '120px'}}>{t('tos.intoEffectAt')}</Table.Th>
                                {userProfile.isOrganizationManager && <Table.Th style={{minWidth: '120px'}}>{t('tos.approved')}</Table.Th>}
                                <Table.Th style={{minWidth: '200px'}}>{t('actions')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{rows}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>
        </>
    );
}

export default ToSList;
